import React, { useState, useEffect } from "react";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import { motion } from "framer-motion";
import { ChevronRight, ChevronDown } from "lucide-react";
import { Zap, Shield, Sparkles, ArrowRight } from "lucide-react";
import googleLogo from "../assets/images/logos/google.svg";
import microsoftLogo from "../assets/images/logos/microsoft.svg";
import slackLogo from "../assets/images/logos/slack.svg";
import githubLogo from "../assets/images/logos/github.svg";
import dropboxLogo from "../assets/images/logos/dropbox.svg";
import salesforceLogo from "../assets/images/logos/salesforce.svg";

const PricingPlanProps = {
  plan: {
    name: String,
    monthlyPrice: String,
    annualPrice: String,
    period: String,
    monthlyBilling: String,
    annualBilling: String,
    note: String,
    features: [String],
    cta: String,
    highlight: Boolean,
  },
  darkMode: Boolean,
  annualBilling: Boolean,
  highlight: Boolean,
};

const TeamPlanCalculator = ({ darkMode, annualBilling }) => {
  const [userCount, setUserCount] = useState(3);
  const monthlyPrice = 15.99;
  const annualPrice = 12.99;

  const calculateCost = () => {
    const basePrice = annualBilling ? annualPrice : monthlyPrice;
    return (basePrice * userCount).toFixed(2);
  };

  return (
    <div className="mt-6">
      <label
        htmlFor="user-count"
        className="block mb-2 text-sm font-medium text-gray-800"
      >
        Number of Users: {userCount}
      </label>
      <input
        id="user-count"
        type="range"
        min="3"
        max="100"
        value={userCount}
        onChange={(e) => setUserCount(Number(e.target.value))}
        className="w-full h-2 bg-gradient-to-r from-indigo-200 to-indigo-300 rounded-lg appearance-none cursor-pointer"
      />
      <div className="mt-1 mb-2.5 text-center">
        <span className="text-2xl font-bold text-gray-900">
          ${calculateCost()}
        </span>
        <span className="text-sm text-gray-600">
          {annualBilling ? "/month billed annually" : "/month"}
        </span>
      </div>
    </div>
  );
};

function PricingPage() {
  const [darkMode, setDarkMode] = useState(false);
  const [logoFade, setLogoFade] = useState(false);
  const [annualBilling, setAnnualBilling] = useState(true);
  const [openStates, setOpenStates] = useState(new Array(4).fill(false));

  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
  }, [darkMode]);

  const handleDarkModeToggle = () => {
    setLogoFade(true);
    setTimeout(() => {
      setDarkMode(!darkMode);
      setLogoFade(false);
    }, 150);
  };

  const PricingPlan = ({
    plan,
    darkMode,
    annualBilling,
    highlight = false,
  }) => (
    <div
      className={`group relative overflow-hidden rounded-xl border-[1px] ${
        highlight
          ? "border-purple-500/20 dark:border-purple-300/20"
          : "border-black/5 dark:border-white/5"
      } bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-6 transition-all duration-300 hover:shadow-xl`}
    >
      <div className="relative space-y-4 h-full flex flex-col">
        <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100">
          {plan.name}
        </h3>

        <div className="flex items-baseline">
          <span className="text-4xl font-bold text-gray-900 dark:text-gray-100">
            {plan.name === "Boxia Enterprise"
              ? ""
              : annualBilling
                ? plan.annualPrice
                : plan.monthlyPrice}
          </span>
          <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
            {plan.name === "Boxia Enterprise" ? "" : "/month"}
          </span>
        </div>

        {plan.name === "Boxia Enterprise" && (
          <div className="text-xl font-medium text-gray-900 dark:text-gray-100">
            Custom pricing
          </div>
        )}

        <ul className="space-y-3 pt-4 flex-grow">
          {plan.features.map((feature, idx) => (
            <li
              key={idx}
              className="flex items-center text-sm text-gray-600 dark:text-gray-400"
            >
              <div className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400" />
              {feature}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  const plans = [
    // {
    //   name: "Boxia Personal",
    //   monthlyPrice: "Free",
    //   annualPrice: "Free",
    //   period: "",
    //   monthlyBilling: "",
    //   annualBilling: "",
    //   features: [
    //     "Basic productivity tools (Calendar, Notes, To-Do, Files)",
    //     "Single-user account",
    //     "50GB storage",
    //     "Community support",
    //     "Unlimited Boxia Kairo Basic",
    //     "Basic third-party integrations (up to 5)",
    //   ],
    // },
    {
      name: "Boxia Pro",
      monthlyPrice: "$12.99",
      annualPrice: "$9.99",
      period: "/month",
      monthlyBilling: "billed monthly",
      annualBilling: "billed annually",
      features: [
        "Everything in Personal plus:",
        "Advanced productivity tools (Docs, Sheets, Slides)",
        "Priority support",
        "Unlimited storage",
        "Unlimited Boxia Kairo Advanced",
        "Unlimited advanced integrations",
      ],
      highlight: true,
    },
    {
      name: "Boxia Teams",
      monthlyPrice: "$12.99",
      annualPrice: "$12.99",
      period: "/user/month",
      monthlyBilling: "billed monthly",
      annualBilling: "billed annually",
      note: "Minimum 3 users",
      features: [
        "Everything in Pro plus:",
        "Shared workspaces and collaborative tools",
        "Team analytics and insights",
        "Admin controls and role management",
        "Enhanced security features",
        "Unlimited shared storage",
      ],
    },
    {
      name: "Boxia Enterprise",
      monthlyPrice: "Custom",
      annualPrice: "Custom",
      period: "Custom",
      features: [
        "Everything in Team plus:",
        "Custom integrations and workflows",
        "On-premises deployment option",
        "Advanced security (SSO, audit logs, custom permissions)",
        "Dedicated account manager",
        "Custom onboarding and training",
      ],
    },
  ];

  return (
    <main className="flex-grow overflow-y-auto">
      <NavBar darkMode={darkMode} logoFade={logoFade} />

      {/* Hero Section */}
      <section className="relative pt-[160px]">
        <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
          <div className="space-y-5 max-w-3xl mx-auto text-center">
            <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-xl w-fit">
              Pricing
              <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
            </h1>
            <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
              Simple Pricing for{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                Powerful Productivity
              </span>
            </h2>
            <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
              Choose the plan that fits your needs and start boosting your
              productivity today
            </p>
          </div>

          {/* Billing Toggle */}
          <div className="flex justify-center mt-10">
            <div className="inline-flex items-center rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-1">
              <button
                className={`px-6 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  annualBilling
                    ? "bg-gradient-to-tr from-purple-600/80 to-pink-500/80 dark:from-purple-300/80 dark:to-orange-200/80 text-white shadow-lg"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                }`}
                onClick={() => setAnnualBilling(true)}
              >
                Annual Billing
              </button>
              <button
                className={`px-6 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  !annualBilling
                    ? "bg-gradient-to-tr from-purple-600/80 to-pink-500/80 dark:from-purple-300/80 dark:to-orange-200/80 text-white shadow-lg"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                }`}
                onClick={() => setAnnualBilling(false)}
              >
                Monthly Billing
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="relative">
        <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map((plan, index) => (
              <PricingPlan
                key={index}
                plan={plan}
                darkMode={darkMode}
                annualBilling={annualBilling}
                highlight={plan.highlight}
              />
            ))}
          </div>

          {/* Single CTA Button */}
          <div className="text-center mt-16">
            <a
              href="https://app.boxia.io/auth/signup"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 text-base font-medium rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200 text-white shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Start your 14-day trial
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 duration-300" />
            </a>
          </div>
        </div>
      </section>

      {/* Features Comparison Section */}
      <section className="relative">
        <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
          <div className="space-y-5 max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-5xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
              Compare{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                Features
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {["Pro", "Team", "Enterprise"].map(
              (plan, planIndex) => (
                <div
                  key={planIndex}
                  className="group relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-6"
                >
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-6">
                    {plan}
                  </h3>
                  <ul className="space-y-4">
                    {plans[planIndex].features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center">
                        <svg
                          className="w-5 h-5 mr-2 text-green-500 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span
                          className={`text-sm ${
                            darkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ),
            )}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="relative">
        <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
          <div className="space-y-5 max-w-3xl mx-auto text-center">
            <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-xl w-fit">
              FAQ
              <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
            </h1>
            <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
              Got{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                Questions?
              </span>
            </h2>
            <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
              Find answers to common questions about Boxia
            </p>
          </div>

          <div className="max-w-3xl mx-auto mt-16 space-y-4">
            {[
              {
                question: "Is there a free plan available?",
                answer:
                  "We don't currently offer a free plan, but we're working on one! For now, we offer a 14-day free trial for all our paid plans. After the trial, you'll be automatically charged for the plan you selected. If you cancel your subscription, you won't be able to access Boxia until you resubscribe.",
              },
              {
                question: "Can I change my plan later?",
                answer:
                  "Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle. Any unused portion of paid subscriptions will be refunded on a prorated basis.",
              },
              {
                question: "What payment methods do you accept?",
                answer:
                  "We accept all major credit cards and debit cards through Stripe. For Enterprise plans, we also support invoicing and wire transfers.",
              },
              {
                question: "Is there a free trial available?",
                answer:
                  "Yes, we offer a 14-day free trial for all our paid plans. No credit card is required to start your trial. At the end of the trial, you'll be automatically charged for the plan you selected. If you cancel your subscription, you won't be able to access Boxia until you resubscribe.",
              },
              {
                question: "What happens when my trial ends?",
                answer:
                  "At the end of your trial, you'll be automatically charged for the plan you selected. If you cancel your subscription, you won't be able to access Boxia until you resubscribe. We're working on a free plan that will be available soon!",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 transition-all duration-300 hover:shadow-xl"
              >
                <button
                  onClick={() => {
                    const newOpenStates = [...openStates];
                    newOpenStates[index] = !newOpenStates[index];
                    setOpenStates(newOpenStates);
                  }}
                  className="flex w-full items-center justify-between p-6 text-left"
                >
                  <span className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {item.question}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${
                      openStates[index] ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openStates[index] ? "max-h-96" : "max-h-0"
                  }`}
                >
                  <p className="px-6 pb-6 text-gray-600 dark:text-gray-400">
                    {item.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative">
        <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
          <div className="space-y-5 max-w-3xl mx-auto text-center">
            <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-xl w-fit">
              Alpha Access
              <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
            </h1>
            <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
              Be Part of the Future of{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                Work
              </span>
            </h2>
            <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
              Join the Boxia Alpha and be among the first to experience Boxia's
              revolutionary productivity suite
            </p>

            <div className="mt-10 flex flex-col items-center gap-4">
              <a
                href="https://app.boxia.io/auth/signup"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 text-base font-medium rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200 text-white shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Sign Up Now
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 duration-300" />
              </a>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                 
              </span>
            </div>
          </div>

          <div className="mt-20 space-y-10">
            <div className="relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-8">
              <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-8 text-center">
                Trusted by Industry Leaders
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center justify-center">
                {[
                  { name: "google", logo: googleLogo },
                  { name: "microsoft", logo: microsoftLogo },
                  { name: "slack", logo: slackLogo },
                  { name: "dropbox", logo: dropboxLogo },
                  { name: "github", logo: githubLogo },
                  { name: "salesforce", logo: salesforceLogo },
                ].map((company) => (
                  <div
                    key={company.name}
                    className="flex items-center justify-center group"
                  >
                    <img
                      src={company.logo}
                      alt={`${company.name} logo`}
                      className="w-auto h-8 opacity-50 group-hover:opacity-100 transition-opacity duration-300 dark:invert dark:brightness-0 dark:contrast-200"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  icon: Zap,
                  title: "Lightning Fast",
                  description:
                    "Experience instant response times and real-time collaboration",
                },
                {
                  icon: Shield,
                  title: "Enterprise Ready",
                  description:
                    "Bank-level security with SOC 2 Type II certification",
                },
                {
                  icon: Sparkles,
                  title: "AI Powered",
                  description:
                    "Advanced AI features to supercharge your productivity",
                },
              ].map((feature, index) => (
                <div
                  key={index}
                  className="group relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-6 text-center"
                >
                  <div className="inline-block rounded-lg bg-gradient-to-tr from-zinc-300/30 via-purple-400/30 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20 p-3 mb-4">
                    <feature.icon className="w-6 h-6 text-gray-900 dark:text-gray-100" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer darkMode={darkMode} />
    </main>
  );
}

export default PricingPage;
